import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { HintTooltip, useIsMobile } from '@brg/ui';
import { createPortal } from 'react-dom';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { OverlayBoundary } from '../../editor/EditorOverlay/styles';
import MultipleColumn from '../../editor/TwoPicturesBlock/components/MultipleColumn';
import useLocalization from '../../../../../hooks/useLocalization';
import { CaptionMarkdownText } from '../whitepostblock';
import formatLinks from '../../../../../utilites/formatLinks';
import { MobileLoaderWrapper } from '../../addimageblock';
import { LoaderModal } from '../../../../modals/LoaderModal';
import { LoaderProgressBar } from '../imagecaptionblock';
import { GeneralEditorOverlay } from '../../editor/EditorOverlayNew/GeneralEditorOverlay';
import { fixUnsizedImages, isUnsizedImages } from '../../../../../utilites/fixUnsizedImages';
import useCommonProps from '../../../../../hooks/useCommonProps';
import { getPostWithoutOriginals } from '../../../../../utilites/getPostWithoutOriginals';
import { CaptionBlock, GridContainer, GridItem, Image } from './style';
import ResizedImageEnhanced from '../../../../ResizedImageEnhanced';
import AspectRatio from '../../../../AspectRatio';
import { getTileViewType, TileViewType } from '../../../../../constants/TyleViewType';
var getDataImgUrl = function (data) { var _a; return ((data === null || data === void 0 ? void 0 : data.src) ? data === null || data === void 0 ? void 0 : data.src : (_a = data === null || data === void 0 ? void 0 : data.file) === null || _a === void 0 ? void 0 : _a.url); };
var TileBlock = function (props) {
    var post = props.post, srcList = props.srcList, isEditable = props.isEditable, dragHandleProps = props.dragHandleProps, onChange = props.onChange, onSlideTextChange = props.onSlideTextChange, onRatioError = props.onRatioError, controlsPlacer = props.controlsPlacer, onCaptionEdit = props.onCaptionEdit, colorStyle = props.colorStyle, onChangeImage = props.onChangeImage, fileRef = props.fileRef, isTwoPicturesLoading = props.isLoading;
    var t = useLocalization().t;
    var _a = useState(false), editingText = _a[0], setEditingText = _a[1];
    var _b = useState(null), position = _b[0], setPosition = _b[1];
    var _c = useState(null), editedSlide = _c[0], setEditedSlide = _c[1];
    var editedSlideRef = useRef(null);
    var _d = useState([]), listWithSizes = _d[0], setListWithSizes = _d[1];
    var _e = useState(false), checkSizeLoad = _e[0], setCheckSizeLoad = _e[1];
    var _f = useCommonProps(), isPrint = _f.isPrint, printBg = _f.printBg, isFeed = _f.isFeed, printType = _f.printType;
    useEffect(function () {
        editedSlideRef.current = editedSlide;
    }, [editedSlide]);
    var isMobile = useIsMobile();
    var isLoading = useMemo(function () { return ((post === null || post === void 0 ? void 0 : post.postId) === 'loading' || (post === null || post === void 0 ? void 0 : post.postId) === 'updating' || isTwoPicturesLoading) && !checkSizeLoad; }, [post, isTwoPicturesLoading, checkSizeLoad]);
    var columns = useMemo(function () {
        var _a;
        var type = getTileViewType(post);
        if (isMobile) {
            return ((_a = [TileViewType.fourRow, TileViewType.threeRow]) === null || _a === void 0 ? void 0 : _a.includes(type)) ? 2 : 1;
        }
        if (type === TileViewType.fourRow)
            return 4;
        else if (type === TileViewType.threeRow)
            return 3;
        return 2;
    }, [post, isMobile]);
    var srcListFinal = useMemo(function () { var _a; return (((_a = (isPrint ? getPostWithoutOriginals(post) : post)) === null || _a === void 0 ? void 0 : _a.attachments) || srcList); }, [post, srcList, isPrint]);
    var captionMaxLength = 100;
    useEffect(function () {
        if (isUnsizedImages(srcListFinal)) {
            setCheckSizeLoad(true);
            fixUnsizedImages(srcListFinal || [])
                .then(function (r) {
                setListWithSizes(r || []);
                setCheckSizeLoad(false);
            });
        }
        else {
            setListWithSizes(srcListFinal);
        }
    }, [srcListFinal]);
    if (!(listWithSizes === null || listWithSizes === void 0 ? void 0 : listWithSizes.length) && !isEditable) {
        return null;
    }
    if (isPrint && printType !== 'magazine') {
        return (React.createElement(GridContainer, { columns: columns }, listWithSizes === null || listWithSizes === void 0 ? void 0 : listWithSizes.map(function (image) {
            return (React.createElement(GridItem, null,
                React.createElement(AspectRatio, { ratio: 0.76, maxWidth: '100%' }, image.files && image.files.length ? (React.createElement(ResizedImageEnhanced, { files: image.files })) : (React.createElement(Image, { src: getDataImgUrl(image) })))));
        })));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { style: { position: 'relative' } },
            isLoading && (React.createElement(React.Fragment, null, isMobile || (post === null || post === void 0 ? void 0 : post.postId) === 'updating' ? (React.createElement(MobileLoaderWrapper, { colorStyle: colorStyle },
                React.createElement(LoaderModal, { placeholder: t('modal.loader.isLoading') }))) : (React.createElement(LoaderProgressBar, null)))),
            React.createElement(OverlayBoundary, { className: 'white-post-block', isLoadingPost: isLoading },
                React.createElement(GeneralEditorOverlay, { isEditable: isEditable, isLoading: isLoading, withoutDrag: isFeed, post: post, dragHandleProps: dragHandleProps }),
                React.createElement(GridContainer, { columns: columns }, listWithSizes === null || listWithSizes === void 0 ? void 0 : listWithSizes.map(function (image) {
                    return (React.createElement(GridItem, null,
                        React.createElement(AspectRatio, { ratio: 1, maxWidth: '100%' }, image.files && image.files.length ? (React.createElement(ResizedImageEnhanced, { files: image.files })) : (React.createElement(Image, { src: getDataImgUrl(image) }))),
                        (image === null || image === void 0 ? void 0 : image.text) && (React.createElement(CaptionBlock, { align: 'left', className: 'longread-caption-block', colorStyle: colorStyle },
                            React.createElement(CaptionMarkdownText, { rehypePlugins: [rehypeRaw, rehypeSanitize] }, formatLinks(image === null || image === void 0 ? void 0 : image.text))))));
                }))))));
};
export default TileBlock;
//# sourceMappingURL=TileBlock.js.mapimport React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { HintTooltip, useIsMobile } from '@brg/ui';
import { createPortal } from 'react-dom';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { OverlayBoundary } from '../../editor/EditorOverlay/styles';
import MultipleColumn from '../../editor/TwoPicturesBlock/components/MultipleColumn';
import useLocalization from '../../../../../hooks/useLocalization';
import { CaptionMarkdownText } from '../whitepostblock';
import formatLinks from '../../../../../utilites/formatLinks';
import { MobileLoaderWrapper } from '../../addimageblock';
import { LoaderModal } from '../../../../modals/LoaderModal';
import { LoaderProgressBar } from '../imagecaptionblock';
import { GeneralEditorOverlay } from '../../editor/EditorOverlayNew/GeneralEditorOverlay';
import { fixUnsizedImages, isUnsizedImages } from '../../../../../utilites/fixUnsizedImages';
import useCommonProps from '../../../../../hooks/useCommonProps';
import { getPostWithoutOriginals } from '../../../../../utilites/getPostWithoutOriginals';
import { CaptionBlock, GridContainer, GridItem, Image } from './style';
import ResizedImageEnhanced from '../../../../ResizedImageEnhanced';
import AspectRatio from '../../../../AspectRatio';
import { getTileViewType, TileViewType } from '../../../../../constants/TyleViewType';
var getDataImgUrl = function (data) { var _a; return ((data === null || data === void 0 ? void 0 : data.src) ? data === null || data === void 0 ? void 0 : data.src : (_a = data === null || data === void 0 ? void 0 : data.file) === null || _a === void 0 ? void 0 : _a.url); };
var TileBlock = function (props) {
    var post = props.post, srcList = props.srcList, isEditable = props.isEditable, dragHandleProps = props.dragHandleProps, onChange = props.onChange, onSlideTextChange = props.onSlideTextChange, onRatioError = props.onRatioError, controlsPlacer = props.controlsPlacer, onCaptionEdit = props.onCaptionEdit, colorStyle = props.colorStyle, onChangeImage = props.onChangeImage, fileRef = props.fileRef, isTwoPicturesLoading = props.isLoading;
    var t = useLocalization().t;
    var _a = useState(false), editingText = _a[0], setEditingText = _a[1];
    var _b = useState(null), position = _b[0], setPosition = _b[1];
    var _c = useState(null), editedSlide = _c[0], setEditedSlide = _c[1];
    var editedSlideRef = useRef(null);
    var _d = useState([]), listWithSizes = _d[0], setListWithSizes = _d[1];
    var _e = useState(false), checkSizeLoad = _e[0], setCheckSizeLoad = _e[1];
    var _f = useCommonProps(), isPrint = _f.isPrint, printBg = _f.printBg, isFeed = _f.isFeed, printType = _f.printType;
    useEffect(function () {
        editedSlideRef.current = editedSlide;
    }, [editedSlide]);
    var isMobile = useIsMobile();
    var isLoading = useMemo(function () { return ((post === null || post === void 0 ? void 0 : post.postId) === 'loading' || (post === null || post === void 0 ? void 0 : post.postId) === 'updating' || isTwoPicturesLoading) && !checkSizeLoad; }, [post, isTwoPicturesLoading, checkSizeLoad]);
    var columns = useMemo(function () {
        var _a;
        var type = getTileViewType(post);
        if (isMobile) {
            return ((_a = [TileViewType.fourRow, TileViewType.threeRow]) === null || _a === void 0 ? void 0 : _a.includes(type)) ? 2 : 1;
        }
        if (type === TileViewType.fourRow)
            return 4;
        else if (type === TileViewType.threeRow)
            return 3;
        return 2;
    }, [post, isMobile]);
    var srcListFinal = useMemo(function () { var _a; return (((_a = (isPrint ? getPostWithoutOriginals(post) : post)) === null || _a === void 0 ? void 0 : _a.attachments) || srcList); }, [post, srcList, isPrint]);
    var captionMaxLength = 100;
    useEffect(function () {
        if (isUnsizedImages(srcListFinal)) {
            setCheckSizeLoad(true);
            fixUnsizedImages(srcListFinal || [])
                .then(function (r) {
                setListWithSizes(r || []);
                setCheckSizeLoad(false);
            });
        }
        else {
            setListWithSizes(srcListFinal);
        }
    }, [srcListFinal]);
    if (!(listWithSizes === null || listWithSizes === void 0 ? void 0 : listWithSizes.length) && !isEditable) {
        return null;
    }
    if (isPrint && printType !== 'magazine') {
        return (React.createElement(GridContainer, { columns: columns }, listWithSizes === null || listWithSizes === void 0 ? void 0 : listWithSizes.map(function (image) {
            return (React.createElement(GridItem, null,
                React.createElement(AspectRatio, { ratio: 0.76, maxWidth: '100%' }, image.files && image.files.length ? (React.createElement(ResizedImageEnhanced, { files: image.files })) : (React.createElement(Image, { src: getDataImgUrl(image) })))));
        })));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { style: { position: 'relative' } },
            isLoading && (React.createElement(React.Fragment, null, isMobile || (post === null || post === void 0 ? void 0 : post.postId) === 'updating' ? (React.createElement(MobileLoaderWrapper, { colorStyle: colorStyle },
                React.createElement(LoaderModal, { placeholder: t('modal.loader.isLoading') }))) : (React.createElement(LoaderProgressBar, null)))),
            React.createElement(OverlayBoundary, { className: 'white-post-block', isLoadingPost: isLoading },
                React.createElement(GeneralEditorOverlay, { isEditable: isEditable, isLoading: isLoading, withoutDrag: isFeed, post: post, dragHandleProps: dragHandleProps }),
                React.createElement(GridContainer, { columns: columns }, listWithSizes === null || listWithSizes === void 0 ? void 0 : listWithSizes.map(function (image) {
                    return (React.createElement(GridItem, null,
                        React.createElement(AspectRatio, { ratio: 1, maxWidth: '100%' }, image.files && image.files.length ? (React.createElement(ResizedImageEnhanced, { files: image.files })) : (React.createElement(Image, { src: getDataImgUrl(image) }))),
                        (image === null || image === void 0 ? void 0 : image.text) && (React.createElement(CaptionBlock, { align: 'left', className: 'longread-caption-block', colorStyle: colorStyle },
                            React.createElement(CaptionMarkdownText, { rehypePlugins: [rehypeRaw, rehypeSanitize] }, formatLinks(image === null || image === void 0 ? void 0 : image.text))))));
                }))))));
};
export default TileBlock;
//# sourceMappingURL=TileBlock.js.map